<template>
  <div>
    <div id="doctor-ai" class="fixed bottom-0 right-0 p-4">
      <div class="superdoc-trigger-pulse">
        <div class="superdoc-trigger cursor-pointer shadow-lg drop-shadow-lg">
          <img class="superdoc-trigger__icon" src="@public/assets/images/ic_superdoc_ava.svg" alt="SuperDoc" />
        </div>
      </div>
    </div>
    <SuperDocDrawer
      ref="superDocDrawer"
      :visible.sync="drawer"
      :direction="direction"
      :processing="processing"
      :ai-welcome-message="aiWelcomeMessage"
      :messages="messages"
      :re-sending="reSending"
      :thinking="thinking"
      :find-time="findTime"
      :deep-thinking-duration-text="appUtils.formatDurationMoment(deepThinkingTime, $lang)"
      :question.sync="question"
      :markdown-renderer="compiledMarkdown"
      @close="close"
      @resend="reSendMessage"
      @send="sendMessage"
    />
  </div>
</template>
<script>
import superdocService from '@/services/superdocService'
import appUtils from '@/utils/appUtils'
import lvmmedService from '@/services/lvmmedService'
import { formatSuperDocHtml } from '@/utils/superdocFormatter'
import { getTypeConfig } from './doctorAIConfig'
import SuperDocDrawer from './SuperDocDrawer.vue'

export default {
  name: 'DoctorAI',
  components: { SuperDocDrawer },
  props: {
    /** Nội dung câu hỏi mặc định (auto-send khi mở, nếu chưa có cache) */
    issue: String,
    /** Loại context: BENH_AN | HOI_CHAN | ... (xem doctorAIConfig.js) */
    type: {
      type: String,
      default: ''
    },
    /** HOI_CHAN: session ID */
    sessionId: [String, Number],
    /** HOI_CHAN: disease ID */
    diseaseId: [String, Number],
    /** BENH_AN: bệnh án ID (BADT) */
    badtId: [String, Number],
    hasResult: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      appUtils,
      question: '',
      messages: [],
      history: [],
      thinking: false,
      processing: false,
      drawer: false,
      direction: 'rtl',
      aiWelcomeMessage: '',
      reSending: false,
      askAbortController: null,
      findTime: null,
      deepThinkingTime: null,
      remoteSessionId: null
    }
  },
  computed: {
    typeConfig () {
      return getTypeConfig(this.type)
    }
  },
  watch: {
    hasError: {
      handler (newVal) {
        if (newVal && this.drawer) {
          const lastIdx = this.messages.length - 1
          if (lastIdx >= 0) {
            this.$set(this.messages[lastIdx], 'error', true)
          }
          this.thinking = false
          this.processing = false
        }
      },
      deep: true
    }
  },
  mounted () {
    this.$nextTick(() => {
      const chatButton = document.getElementById('doctor-ai')
      if (chatButton) {
        chatButton.addEventListener('click', this.handleClickFloatingButton)
      }
    })
  },
  methods: {
    async ensureLvmmedLinkedForAskAi () {
      try {
        const status = await lvmmedService.getLinkStatus()
        if (Number(status?.link_status) === 1) return true
      } catch (error) {
        console.log(error)
      }

      try {
        await this.$confirm(
          'Bạn cần liên kết tài khoản LVMMED trước khi sử dụng Ask AI.',
          'Chưa liên kết LVMMED',
          {
            confirmButtonText: 'Đi tới Thiết lập cá nhân',
            cancelButtonText: 'Để sau',
            type: 'warning'
          }
        )
        this.$router.push({ path: '/doctor/setting', query: { type: 'lvmmed-link' } }).catch(() => {})
      } catch (e) {}

      return false
    },
    async handleClickFloatingButton () {
      const isLinked = await this.ensureLvmmedLinkedForAskAi()
      if (!isLinked) {
        return
      }
      this.drawer = true
      this.onDrawerOpen()
    },
    getAskMetadata () {
      if (this.type !== 'BENH_AN' || !this.badtId) return ''
      const metadataPayload = {
        badtId: String(this.badtId)
      }
      return JSON.stringify(metadataPayload)
    },
    async loadRemoteMessagesByBadt () {
      if (this.type !== 'BENH_AN' || !this.badtId) return false

      const metaFilter = JSON.stringify([{ key: 'badtId', value: `${this.badtId}`, op: 'eq' }])
      const historyRes = await superdocService.getHistory({
        limit: 1,
        page: 1,
        meta_filter: metaFilter
      })

      const sessions = historyRes?.data?.data || historyRes?.data || []
      if (!Array.isArray(sessions) || !sessions.length) return false

      const historyItem = sessions[0]
      const historyId = historyItem?.id || historyItem?.session_id
      if (!historyId) return false

      this.remoteSessionId = String(historyId)
      const messagesRes = await superdocService.getSessionMessages(historyId)
      const fromMessagesApi = messagesRes?.data?.data || messagesRes?.data?.messages || messagesRes?.data || []
      const normalizedMessages = Array.isArray(fromMessagesApi)
        ? fromMessagesApi
          .filter(item => item?.question || item?.answer)
          .map(item => ({
            question: item.question || '',
            answer: item.answer || ''
          }))
        : []

      if (!normalizedMessages.length) return false

      this.messages = normalizedMessages
      this.history = normalizedMessages.map(item => ({ question: item.question, answer: item.answer }))
      this.scrollToLastMessage()
      return true
    },
    resolveSessionIdFromStreamEvent (streamEvent) {
      const parsed = streamEvent?.parsed
      const sessionId = parsed?.session_id || parsed?.sessionId || parsed?.data?.session_id || parsed?.meta?.session_id
      if (sessionId && !this.remoteSessionId) {
        this.remoteSessionId = String(sessionId)
      }
    },
    async onDrawerOpen () {
      this.processing = true
      this.remoteSessionId = null
      try {
        this.aiWelcomeMessage = this.issue
          ? this.$t('lbl_super_doc.lbl_welcome_msg')
          : this.typeConfig.getWelcomeMessageNoIssue(this)

        let hasRemoteMessages = false
        if (this.type === 'BENH_AN' && this.badtId) {
          try {
            hasRemoteMessages = await this.loadRemoteMessagesByBadt()
          } catch (error) {
            hasRemoteMessages = false
          }
        }

        const issueText = (this.issue || '').trim()
        if (!hasRemoteMessages && this.messages.length === 0 && issueText) {
          await this.sendMessage(issueText)
        }
      } catch (error) {
        console.log(error)
        this.$message.error('Không thể khởi tạo Ask AI. Vui lòng thử lại.')
        this.drawer = false
      } finally {
        this.processing = false
      }
    },
    normalizeUploadPayload (payload = {}) {
      const images = (payload.images || []).filter(Boolean).map((file) => ({
        file,
        name: file.name || 'image',
        url: URL.createObjectURL(file),
        size: file.size || 0
      }))
      const files = (payload.files || []).filter(Boolean).map((file) => ({
        file,
        name: file.name || 'file',
        url: URL.createObjectURL(file),
        size: file.size || 0
      }))
      return { images, files }
    },
    releaseMessageUploads (message) {
      ;[...(message?.images || []), ...(message?.files || [])].forEach((item) => {
        if (item?.url && item.file) URL.revokeObjectURL(item.url)
      })
    },
    async runAskStream ({ question, messageIndex, uploads = {}, isResend = false }) {
      this.thinking = true
      this.reSending = isResend
      this.findTime = null
      this.deepThinkingTime = null

      try {
        if (this.askAbortController) {
          this.askAbortController.abort()
        }
        this.askAbortController = new AbortController()

        await superdocService.askStream({
          lang: this.$lang || 'vi',
          question,
          agentType: 'type_1.2',
          sessionId: this.remoteSessionId,
          metadata: this.getAskMetadata(),
          files: [...(uploads.files || []), ...(uploads.images || [])],
          signal: this.askAbortController.signal,
          onChunk: (streamEvent) => {
            const message = this.messages[messageIndex]
            if (!message) return
            delete message.error
            this.resolveSessionIdFromStreamEvent(streamEvent)
            if (streamEvent.mode === 'replace') {
              message.answer = streamEvent.text || ''
            } else if (streamEvent.text) {
              message.answer = `${message.answer || ''}${streamEvent.text}`
            }
            this.scrollToLastMessage()
          }
        })

        const message = this.messages[messageIndex]
        if (message) {
          delete message.error
          this.history.push({ question, answer: message.answer || '' })
        }
      } catch (e) {
        const message = this.messages[messageIndex]
        if (message) message.error = true
        if (e?.name !== 'AbortError') {
          this.$message.error('An error occurred. Please try again later.')
        }
      } finally {
        this.askAbortController = null
        this.thinking = false
        this.reSending = false
      }
    },
    async reSendMessage (question, index) {
      if (!question) return
      if (!this.messages[index]) return

      this.releaseMessageUploads(this.messages[index])
      this.messages[index].answer = ''
      await this.runAskStream({
        question,
        messageIndex: index,
        isResend: true
      })
    },
    async sendMessage (question, payload = {}) {
      const askQuestion = (question || '').trim()
      const hasUploads = Boolean(payload.files?.length || payload.images?.length)
      if (!askQuestion && !hasUploads) return

      const normalizedUploads = this.normalizeUploadPayload(payload)
      this.thinking = true
      this.findTime = null
      this.deepThinkingTime = null
      this.question = ''
      this.messages.push({
        question: askQuestion,
        answer: '',
        images: normalizedUploads.images.map(item => ({ name: item.name, url: item.url, file: item.file, size: item.size })),
        files: normalizedUploads.files.map(item => ({ name: item.name, url: item.url, file: item.file, size: item.size }))
      })

      await this.runAskStream({
        question: askQuestion,
        messageIndex: this.messages.length - 1,
        uploads: payload
      })
    },
    cancelRequest () {
      if (this.askAbortController) {
        this.askAbortController.abort()
        this.askAbortController = null
      }
    },
    compiledMarkdown (message) {
      return formatSuperDocHtml(message)
    },
    scrollToLastMessage () {
      this.$refs.superDocDrawer && this.$refs.superDocDrawer.scrollToLastMessage()
    },
    close () {
      this.thinking = false
      this.aiWelcomeMessage = ''
      this.remoteSessionId = null
      this.messages.forEach(message => this.releaseMessageUploads(message))
    }
  },
  beforeDestroy () {
    const chatButton = document.getElementById('doctor-ai')
    if (chatButton) {
      chatButton.removeEventListener('click', this.handleClickFloatingButton)
    }
    this.messages.forEach(message => this.releaseMessageUploads(message))
    this.cancelRequest()
  }
}
</script>
<style scoped>
.superdoc-trigger-pulse {
  animation: superdocPulseLoop 5s ease-in-out infinite;
  transform-origin: center;
}

.superdoc-trigger {
  width: 64px;
  height: 64px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #eef5ff 0%, #dbeafe 100%);
  border: 1px solid #bfdbfe;
  animation: superdocHeartbeat 1.4s ease-in-out infinite;
  transform-origin: center;
}

.superdoc-trigger__icon {
  width: 38px;
  height: 38px;
  object-fit: contain;
}

@keyframes superdocHeartbeat {
  0%,
  100% {
    transform: scale(1);
  }

  14% {
    transform: scale(1.05);
  }

  28% {
    transform: scale(0.98);
  }

  42% {
    transform: scale(1.04);
  }

  56% {
    transform: scale(0.99);
  }
}

@keyframes superdocPulseLoop {
  0%,
  84%,
  100% {
    transform: scale(1);
  }

  90% {
    transform: scale(1.15);
  }

  95% {
    transform: scale(0.97);
  }
}
</style>
